import React from 'react'
import ReactDOM from 'react-dom/client'
import { RouterProvider } from '@tanstack/react-router'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { MantineProvider, ColorSchemeScript } from '@mantine/core'
import { Notifications } from '@mantine/notifications'
import { ModalsProvider } from '@mantine/modals'
import { DatesProvider } from '@mantine/dates'
import { useTranslation } from 'react-i18next'
import '@mantine/core/styles.css'
import '@mantine/notifications/styles.css'
import '@mantine/dates/styles.css'
import '@mantine/carousel/styles.css'
import { theme } from './theme'
import './styles/globals.css'
import './i18n'

// Import the router and theme
import { router } from './router'
import { queryClient } from './lib/queryClient'
import { bootMockModeIfEnabled } from './stores/mockMode'

// If the user had mock mode toggled on in a previous session, start the worker
// before the first network request fires. Failures fall back to staging silently.
void bootMockModeIfEnabled()

// Register the router instance for type safety
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
}

const LocalizedDatesProvider = ({ children }: { children: React.ReactNode }) => {
  const { i18n } = useTranslation()
  return <DatesProvider settings={{ locale: i18n.language }}>{children}</DatesProvider>
}

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <ColorSchemeScript />
    <QueryClientProvider client={queryClient}>
      <MantineProvider defaultColorScheme="dark" theme={theme}>
        <LocalizedDatesProvider>
          <ModalsProvider>
            <Notifications />
            <RouterProvider router={router} />
            <ReactQueryDevtools initialIsOpen={false} />
          </ModalsProvider>
        </LocalizedDatesProvider>
      </MantineProvider>
    </QueryClientProvider>
  </React.StrictMode>
)
